/**
 *  @class
 *  @function SearchForm
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.is_drawer = this.dataset.drawer;
      this.drawer = this.closest('#Search-Drawer');
      this.form = this.querySelector('form');
      this.button = document.querySelectorAll('.thb-quick-search');
      this.input = this.querySelector('.search-field');
      this.defaultTab = this.querySelector('.side-panel-content--initial');
      this.predictiveSearchResults = this.is_drawer ? this.querySelector('.side-panel-content--has-tabs') : this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');
      this.promotionTemplate = this.querySelector('.thb-predictive-search--promotion-template');
      this.promotionHydrated = false;
      this.cache = new Map();
      this.cacheLimit = 50;
      this.lastSearchTerm = '';
      this.viewportBucket = this.getViewportBucket();

      this.setupEventListeners();
      this.setupDrawerLifecycle();
    }

    hydratePromotion() {
      if (!this.promotionTemplate) {
        return;
      }
      this.promotionTemplate.parentNode.insertBefore(
        this.promotionTemplate.content,
        this.promotionTemplate
      );
      this.promotionTemplate.remove();
      this.promotionTemplate = null;
      this.promotionHydrated = true;
      document.dispatchEvent(new CustomEvent('pikeo:search-promotion:hydrated', {
        detail: { drawer: this.drawer }
      }));
    }

    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));
      this.input.addEventListener('focus', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));

      this.button.forEach((item) => {
        item.addEventListener('click', (event) => {
          event.preventDefault();
          this.hydratePromotion();
          if (this.is_drawer) {
            this.refreshDrawerLayout('open');
            OverlayManager.open(this.drawer);
            setTimeout(() => {
              this.input.focus({
                preventScroll: true
              });
            }, 100);
          } else {
            document.body.classList.add('open-cc');
          }
          return false;
        });
      });

      if (!this.is_drawer) {
        this.onCcClickBound = this.close.bind(this);
        this.onKeyUpBound = (e) => {
          if (e.code && e.code.toUpperCase() === 'ESCAPE') {
            this.close();
          }
        };
        this.cc?.addEventListener('click', this.onCcClickBound);
        document.addEventListener('keyup', this.onKeyUpBound);
      }
    }

    setupDrawerLifecycle() {
      if (!this.is_drawer || !this.drawer) {
        return;
      }
      this.onResizeBound = debounce(() => {
        const nextBucket = this.getViewportBucket();
        const bucketChanged = nextBucket !== this.viewportBucket;
        this.viewportBucket = nextBucket;
        this.refreshDrawerLayout(bucketChanged ? 'breakpoint-change' : 'resize', {
          bucketChanged
        });
      }, 120);
      this.onPanelOpenBound = (event) => {
        if (event.detail?.panel !== this.drawer) {
          return;
        }
        this.refreshDrawerLayout('panel-open');
      };
      this.onPanelClosedBound = (event) => {
        if (event.detail?.panel !== this.drawer) {
          return;
        }
        this.refreshDrawerLayout('panel-closed');
      };
      this.breakpointMediaQueries = [
        window.matchMedia('(min-width: 768px)'),
        window.matchMedia('(min-width: 990px)')
      ];
      this.onMediaChangeBound = () => {
        const nextBucket = this.getViewportBucket();
        const bucketChanged = nextBucket !== this.viewportBucket;
        this.viewportBucket = nextBucket;
        this.refreshDrawerLayout(bucketChanged ? 'breakpoint-change' : 'media-change', {
          bucketChanged
        });
      };
      window.addEventListener('resize', this.onResizeBound, { passive: true });
      this.breakpointMediaQueries.forEach((mq) => {
        if (typeof mq.addEventListener === 'function') {
          mq.addEventListener('change', this.onMediaChangeBound);
        }
      });
      document.addEventListener('panel:open', this.onPanelOpenBound);
      document.addEventListener('panel:closed', this.onPanelClosedBound);
    }

    disconnectedCallback() {
      if (this.onKeyUpBound) {
        document.removeEventListener('keyup', this.onKeyUpBound);
      }
      if (this.onCcClickBound && this.cc) {
        this.cc.removeEventListener('click', this.onCcClickBound);
      }
      if (this.onResizeBound) {
        window.removeEventListener('resize', this.onResizeBound);
      }
      if (this.breakpointMediaQueries && this.onMediaChangeBound) {
        this.breakpointMediaQueries.forEach((mq) => {
          if (typeof mq.removeEventListener === 'function') {
            mq.removeEventListener('change', this.onMediaChangeBound);
          }
        });
      }
      if (this.onPanelOpenBound) {
        document.removeEventListener('panel:open', this.onPanelOpenBound);
      }
      if (this.onPanelClosedBound) {
        document.removeEventListener('panel:closed', this.onPanelClosedBound);
      }
    }

    getViewportBucket() {
      if (window.matchMedia('(min-width: 990px)').matches) {
        return 'desktop';
      }
      if (window.matchMedia('(min-width: 768px)').matches) {
        return 'tablet';
      }
      return 'mobile';
    }

    refreshDrawerLayout(reason, extra = {}) {
      if (!this.is_drawer || !this.drawer) {
        return;
      }
      const bucket = this.getViewportBucket();
      this.drawer.dataset.viewport = bucket;
      this.drawer.classList.toggle('search-drawer--mobile', bucket === 'mobile');
      this.drawer.classList.toggle('search-drawer--tablet', bucket === 'tablet');
      this.drawer.classList.toggle('search-drawer--desktop', bucket === 'desktop');
      document.dispatchEvent(new CustomEvent('pikeo:search-drawer:refresh', {
        detail: {
          drawer: this.drawer,
          reason,
          bucket,
          hasQuery: this.getQuery().length > 0,
          ...extra
        }
      }));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.lastSearchTerm = '';
        this.showDefaultContent();
        return;
      }
      if (!this.is_drawer) {
        this.open();
      }
      if (searchTerm === this.lastSearchTerm) {
        return;
      }
      this.lastSearchTerm = searchTerm;
      this.showSearchResults();
      this.getSearchResults(searchTerm);
    }

    showDefaultContent() {
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.remove('active');
        this.predictiveSearchResults.innerHTML = '';
      }
      if (this.defaultTab) {
        this.defaultTab.style.display = '';
      }
      if (!this.is_drawer) {
        this.close();
      }
    }

    showSearchResults() {
      if (this.defaultTab) {
        this.defaultTab.style.display = 'none';
      }
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.add('active');
      }
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    getSearchResults(searchTerm) {
      const cached = this.cache.get(searchTerm);
      if (cached) {
        this.renderSearchResults(cached);
        return;
      }

      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.add('loading');
      }

      fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article,query,page&${encodeURIComponent('resources[limit]')}=10&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku&section_id=predictive-search`)
        .then((response) => {
          if (this.predictiveSearchResults) {
            this.predictiveSearchResults.classList.remove('loading');
          }
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

          if (this.cache.size >= this.cacheLimit) {
            this.cache.delete(this.cache.keys().next().value);
          }
          this.cache.set(searchTerm, resultsMarkup);

          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          throw error;
        });
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      const submitBtn = this.predictiveSearchResults.querySelector('#search-results-submit');
      if (submitBtn) {
        if (!this.is_drawer) {
          submitBtn.classList.remove('button');
        }
        submitBtn.addEventListener('click', () => {
          this.form.submit();
        });
      }
      this.refreshDrawerLayout('results-rendered');
    }

    close() {
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.setAttribute('inert', '');
      }
      document.body.classList.remove('open-search');
    }
    open() {
      document.body.classList.add('open-search');
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.removeAttribute('inert');
      }
    }
  }
  customElements.define('search-form', SearchForm);
}
